import { jobGuidManagerFactory, persistentFallbackTriggerFactory } from '@shipstation/async-client-manager';

import { v4 } from 'uuid';

import { LogService } from '../log-service';
import { checkLabelBatchIsComplete } from '../shipstation-connect/helpers';

const LABEL_PURCHASE_TIMEOUT_RETRY = 3;

const getSafeBatchId = (msg) => {
  return `${msg?.status?.BatchID}`;
};

const completeCheck = (pusherMessage, labelBatchIds) => {
  const batchId = getSafeBatchId(pusherMessage);
  return labelBatchIds.includes(batchId) && checkLabelBatchIsComplete(pusherMessage);
};

const areAllLabelBatchesComplete = (labelBatchStatusTracker) => {
  const statuses = Object.values(labelBatchStatusTracker);
  return statuses.length && statuses.every((status) => status);
};

const resetTimeoutCheck = (pusherMessage, labelBatchIds) => {
  const batchId = getSafeBatchId(pusherMessage);
  return labelBatchIds.includes(batchId);
};

const createScopedHandlers = (startFallback, endFallback) => {
  const internalId = v4();

  const labelBatchStatusTracker = {};

  const timeoutCompleteCheck = ({ messages }) => {
    const keys = Object.keys(labelBatchStatusTracker);
    const completeMessages = messages.filter((msg) => completeCheck(msg, keys));
    completeMessages.forEach((message) => {
      labelBatchStatusTracker[message.status.BatchID] = true;
    });
    return areAllLabelBatchesComplete(labelBatchStatusTracker);
  };
  const timeoutRetryCheck = ({ messages, retryCount }) => {
    return !timeoutCompleteCheck({ messages }) && retryCount < LABEL_PURCHASE_TIMEOUT_RETRY;
  };
  const timeoutFailureCheck = ({ messages, retryCount }) => {
    return !timeoutCompleteCheck({ messages }) && retryCount >= LABEL_PURCHASE_TIMEOUT_RETRY;
  };

  const onTimeout = () => {
    LogService.logInfo('Label purchase timed out', {
      labelBatchIds: Object.keys(labelBatchStatusTracker),
    });
  };

  const socketMessageCompleteCheck = (msg, currentLabelBatchIds) => {
    const batchId = getSafeBatchId(msg);

    const isComplete = completeCheck(msg, currentLabelBatchIds);
    if (isComplete) {
      labelBatchStatusTracker[batchId] = true;
    }
    return areAllLabelBatchesComplete(labelBatchStatusTracker);
  };

  const onStart = (labelBatchIds, logContext = {}) => {
    const jobGuidManager = jobGuidManagerFactory();
    const fallbackOptions = {
      completeCheck: socketMessageCompleteCheck,
      resetTimeoutCheck,
      timeoutCompleteCheck,
      timeoutRetryCheck,
      timeoutFailureCheck,
      onTimeout,
      timeoutMs: 10000,
      jobGuidManager,
      logContext,
    };

    labelBatchIds.forEach((id) => {
      labelBatchStatusTracker[`${id}`] = false;
      jobGuidManager.addJobGuid(id);
    });

    startFallback(labelBatchIds, internalId, fallbackOptions);
  };

  const onFinish = (labelBatchIds) => {
    endFallback(labelBatchIds, internalId);
    labelBatchIds.forEach((id) => {
      delete labelBatchStatusTracker[id];
    });
  };

  return {
    onStart,
    onFinish,
  };
};

const labelBatchListenerFactory = () => {
  const { startFallback, endFallback, fallbackTrigger$ } = persistentFallbackTriggerFactory();

  return {
    createSingleJobScopedHandlers: () => {
      return createScopedHandlers(startFallback, endFallback);
    },
    persistentTrigger$: fallbackTrigger$,
  };
};

export const labelBatchListener = labelBatchListenerFactory();

const createFreightScopedHandlers = (startFallback, endFallback) => {
  const internalId = v4();

  const freightJobStatusTracker = {};

  const areAllFreightJobsComplete = (tracker) => {
    const statuses = Object.values(tracker);
    return statuses.length > 0 && statuses.every((status) => status);
  };

  const freightCompleteCheck = (pusherMessage, jobGuids) => {
    const guid = pusherMessage?.messageGuid;
    const isRelevant = guid && jobGuids.includes(guid);
    if (isRelevant) {
      freightJobStatusTracker[guid] = true;
    }
    return areAllFreightJobsComplete(freightJobStatusTracker);
  };

  const freightResetTimeoutCheck = (pusherMessage, jobGuids) => {
    const guid = pusherMessage?.messageGuid;
    return guid && jobGuids.includes(guid);
  };

  const timeoutCompleteCheck = ({ messages }) => {
    const keys = Object.keys(freightJobStatusTracker);
    messages.forEach((msg) => {
      const guid = msg?.messageGuid;
      if (guid && keys.includes(guid)) {
        freightJobStatusTracker[guid] = true;
      }
    });
    return areAllFreightJobsComplete(freightJobStatusTracker);
  };

  const timeoutRetryCheck = ({ messages, retryCount }) => !timeoutCompleteCheck({ messages }) && retryCount < 3;

  const timeoutFailureCheck = ({ messages, retryCount }) => !timeoutCompleteCheck({ messages }) && retryCount >= 3;

  const onTimeout = () => {
    LogService.logInfo('Freight action timed out', {
      jobGuids: Object.keys(freightJobStatusTracker),
    });
  };

  const onStart = (jobGuids, logContext = {}) => {
    const jobGuidManager = jobGuidManagerFactory();
    const fallbackOptions = {
      completeCheck: freightCompleteCheck,
      resetTimeoutCheck: freightResetTimeoutCheck,
      timeoutCompleteCheck,
      timeoutRetryCheck,
      timeoutFailureCheck,
      onTimeout,
      timeoutMs: 30000,
      jobGuidManager,
      logContext,
    };

    jobGuids.forEach((guid) => {
      freightJobStatusTracker[guid] = false;
      jobGuidManager.addJobGuid(guid);
    });

    startFallback(jobGuids, internalId, fallbackOptions);
  };

  const onFinish = (jobGuids) => {
    endFallback(jobGuids, internalId);
    jobGuids.forEach((guid) => {
      delete freightJobStatusTracker[guid];
    });
  };

  return { onStart, onFinish };
};

const freightListenerFactory = () => {
  const { startFallback, endFallback, fallbackTrigger$ } = persistentFallbackTriggerFactory();
  return {
    createSingleJobScopedHandlers: () => createFreightScopedHandlers(startFallback, endFallback),
    persistentTrigger$: fallbackTrigger$,
  };
};

export const freightListener = freightListenerFactory();
